import { Box, Table, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import * as defaultSelectors from "../../store/selectors/defaultSelector";
import {
  normalizeAnnualDataQualityEnvelope,
  normalizeValue,
  toTitleCase,
} from "./annualDataQualityEnvelope";

const REPORT_DESCRIPTION =
  "Annual Data Quality and Measurement Integrity validates whether annual queue conclusions can be trusted before executive, audit, download, or subscription consumers depend on them.";

const CONFIDENCE_ORDER = Object.freeze({
  high: 4,
  medium: 3,
  low: 2,
  unknown: 1,
});

const getConfidenceClassName = (label = "unknown") => {
  const normalized = String(label).toLowerCase();
  return CONFIDENCE_ORDER[normalized] ? normalized : "unknown";
};

const getPointLabel = (point, index) =>
  point.month || point.label || point.name || point.scope || point.date || `Point ${index + 1}`;

const getNumericKeys = (data = []) => {
  const keys = new Set();
  data.forEach((point) => {
    Object.entries(point || {}).forEach(([key, value]) => {
      if (typeof value === "number" && Number.isFinite(value)) {
        keys.add(key);
      }
    });
  });
  return [...keys].slice(0, 3);
};

const AnnualEmptyState = ({ children }) => (
  <Box className="annualDqEmptyState" role="status">
    <h2>Annual Data Quality report unavailable</h2>
    <p>{children}</p>
  </Box>
);

const AnnualMetadata = ({ metadata }) => {
  const items = [
    ["Year", metadata.year],
    ["Created From", metadata.createdFrom],
    ["Created To", metadata.createdTo],
    ["Generated At", metadata.generatedAt],
    ["Data Version", metadata.dataVersion],
  ].filter(([, value]) => value !== undefined && value !== null && value !== "");

  if (!items.length) return null;

  return (
    <dl className="annualDqMetadata" aria-label="Annual report metadata">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{normalizeValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
};

const AnnualConfidencePanel = ({ confidence }) => {
  const confidenceClassName = getConfidenceClassName(confidence.label);
  const detailGroups = [
    ["Reasons", confidence.reasons],
    ["Missing fields", confidence.missingFields],
    ["Incomplete months", confidence.incompleteMonths],
    ["Blockers", confidence.blockers],
  ].filter(([, values]) => values.length);

  return (
    <section
      className={`annualDqConfidence annualDqConfidence--${confidenceClassName}`}
      aria-labelledby="annual-data-quality-confidence"
    >
      <div>
        <p className="annualDqSectionEyebrow">Confidence</p>
        <h2 id="annual-data-quality-confidence">{toTitleCase(confidence.label)}</h2>
        {confidence.score !== undefined ? (
          <p className="annualDqConfidenceScore">Score: {normalizeValue(confidence.score)}</p>
        ) : null}
      </div>
      {detailGroups.length ? (
        <div className="annualDqConfidenceDetails">
          {detailGroups.map(([label, values]) => (
            <div key={label}>
              <h3>{label}</h3>
              <ul>
                {values.map((value, index) => (
                  <li key={`${label}-${index}`}>{normalizeValue(value)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <p>No confidence caveats were supplied with the annual envelope.</p>
      )}
    </section>
  );
};

const AnnualSummaryCards = ({ cards }) => {
  if (!cards.length) return null;

  return (
    <section aria-labelledby="annual-data-quality-summary">
      <h2 id="annual-data-quality-summary">Summary</h2>
      <div className="annualDqSummaryGrid">
        {cards.map((card) => (
          <article
            key={card.key}
            className={`annualDqSummaryCard annualDqSummaryCard--${card.status || "neutral"}`}
          >
            <h3>{card.label}</h3>
            <strong>
              {card.value}
              {card.unit ? <span> {card.unit}</span> : null}
            </strong>
            {card.change ? <p>{card.change}</p> : null}
            {card.status ? <p className="annualDqStatusText">{toTitleCase(card.status)}</p> : null}
          </article>
        ))}
      </div>
    </section>
  );
};

const AnnualWarnings = ({ warnings }) => {
  if (!warnings.length) return null;

  return (
    <section className="annualDqWarnings" aria-labelledby="annual-data-quality-warnings">
      <h2 id="annual-data-quality-warnings">Warnings</h2>
      <ul>
        {warnings.map((warning) => (
          <li key={warning.key}>
            <strong>{toTitleCase(warning.severity)}</strong>
            <span>{warning.scope ? `${warning.scope}: ` : ""}{warning.message}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

const AnnualDataTable = ({ table }) => {
  if (!table.rows.length) {
    return (
      <section className="annualDqTableSection" aria-labelledby={`${table.key}-heading`}>
        <h2 id={`${table.key}-heading`}>{table.title}</h2>
        <Text fontSize="sm">No rows were supplied for this annual data-quality table.</Text>
      </section>
    );
  }

  return (
    <section className="annualDqTableSection" aria-labelledby={`${table.key}-heading`}>
      <h2 id={`${table.key}-heading`}>{table.title}</h2>
      {table.description ? <p>{table.description}</p> : null}
      <div className="annualDqTableWrap">
        <Table.Root className="stripedTable reportDataTable reportDataTable--compact annualDqTable" size="sm">
          <caption>{table.title}</caption>
          <Table.Header>
            <Table.Row>
              {table.columns.map((column) => (
                <Table.ColumnHeader key={column.id} scope="col">
                  <span className="reportDataTableHeaderText">{column.label}</span>
                </Table.ColumnHeader>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {table.rows.map((row, rowIndex) => (
              <Table.Row key={`${table.key}-${rowIndex}`}>
                {table.columns.map((column, columnIndex) => (
                  <Table.Cell key={column.id}>
                    {columnIndex === 0 ? (
                      <strong>{normalizeValue(row[column.id])}</strong>
                    ) : (
                      normalizeValue(row[column.id])
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </div>
    </section>
  );
};

const AnnualChart = ({ chart }) => {
  if (!chart.data.length) {
    return (
      <article className="annualDqChart" aria-labelledby={`${chart.key}-heading`}>
        <h3 id={`${chart.key}-heading`}>{chart.title}</h3>
        <p>No chart data was supplied.</p>
      </article>
    );
  }

  const numericKeys = getNumericKeys(chart.data);
  if (!numericKeys.length) {
    return (
      <article className="annualDqChart" aria-labelledby={`${chart.key}-heading`}>
        <h3 id={`${chart.key}-heading`}>{chart.title}</h3>
        <p>Chart data did not include numeric values to plot.</p>
      </article>
    );
  }

  const primaryMetric = numericKeys[0];
  const maxValue = Math.max(...chart.data.map((point) => point[primaryMetric] || 0), 1);

  return (
    <article className="annualDqChart" aria-labelledby={`${chart.key}-heading`}>
      <h3 id={`${chart.key}-heading`}>{chart.title}</h3>
      {chart.description ? <p>{chart.description}</p> : null}
      <div
        className="annualDqChartBars"
        role="img"
        aria-label={`${chart.title} ${toTitleCase(primaryMetric)} by period`}
      >
        {chart.data.slice(0, 12).map((point, index) => {
          const value = point[primaryMetric] || 0;
          const width = `${Math.max((value / maxValue) * 100, value > 0 ? 3 : 0)}%`;
          return (
            <div key={`${chart.key}-${index}`} className="annualDqChartBarRow">
              <span>{getPointLabel(point, index)}</span>
              <div className="annualDqChartTrack">
                <div className="annualDqChartBar" style={{ width }} />
              </div>
              <strong>{normalizeValue(value)}</strong>
            </div>
          );
        })}
      </div>
      <p className="annualDqChartNote">
        Primary metric: {toTitleCase(primaryMetric)}
        {numericKeys.length > 1 ? `; also supplied: ${numericKeys.slice(1).map(toTitleCase).join(", ")}` : ""}
      </p>
    </article>
  );
};

const AnnualCharts = ({ charts }) => {
  if (!charts.length) return null;

  return (
    <section aria-labelledby="annual-data-quality-charts">
      <h2 id="annual-data-quality-charts">Charts</h2>
      <div className="annualDqChartGrid">
        {charts.map((chart) => (
          <AnnualChart key={chart.key} chart={chart} />
        ))}
      </div>
    </section>
  );
};

const AnnualDataQualityMeasurementIntegrity = ({
  reportIndex = "**REPORT_INDEX**",
  title = "Annual Data Quality and Measurement Integrity",
  description = REPORT_DESCRIPTION,
  emptyTableText = "No annual data-quality tables were supplied.",
}) => {
  const reportsData = useSelector(defaultSelectors.selectReportsData);
  const envelope = useMemo(
    () => normalizeAnnualDataQualityEnvelope(reportsData?.data),
    [reportsData?.data]
  );

  if (!envelope.hasEnvelope) {
    return (
      <AnnualEmptyState>
        The renderer expected the ANR-8 envelope at data.metadata, data.confidence,
        data.summaryCards, data.tables, data.charts, and data.warnings.
      </AnnualEmptyState>
    );
  }

  return (
    <Box className="annualDqReport">
      <header className="annualDqHeader">
        <h1>{reportIndex}. {title}</h1>
        <p>{description}</p>
        <AnnualMetadata metadata={envelope.metadata} />
      </header>

      <AnnualConfidencePanel confidence={envelope.confidence} />
      <AnnualWarnings warnings={envelope.warnings} />
      <AnnualSummaryCards cards={envelope.summaryCards} />
      <AnnualCharts charts={envelope.charts} />

      <section aria-labelledby="annual-data-quality-tables">
        <h2 id="annual-data-quality-tables">Tables</h2>
        {envelope.tables.length ? (
          envelope.tables.map((table) => (
            <AnnualDataTable key={table.key} table={table} />
          ))
        ) : (
          <Text fontSize="sm">{emptyTableText}</Text>
        )}
      </section>
    </Box>
  );
};

export default AnnualDataQualityMeasurementIntegrity;
